import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import styles from './list.module.css';
import { Dialog, Popover } from '@mui/material';
import { ChevronDown, ChevronUp, Code, Edit, Trash2, User, X, Send, CheckCircle, Clock, AlertTriangle, AlertCircle, MoreVertical } from 'react-feather';
import { useState, Fragment, useEffect, useRef, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import { useAuth0 } from '@auth0/auth0-react';
import { Dropdown } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { TableLoader } from '../../../../../components/LoaderC';
import { buildFilterParams } from '../../../vehicleQuery';
import { guestPassDisplay, useSendPaymentLink } from '../../guestPass';
import MobileViewList from './MobileViewList';

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;

// Pass badge colors / status icon per guestPassDisplay tone
const PASS_TONES = {
  active: { color: '#059669', background: '#D1FAE5', icon: <CheckCircle size={14} style={{ color: '#059669' }} /> },
  awaiting: { color: '#D97706', background: '#FEF3C7', icon: <Clock size={14} style={{ color: '#D97706' }} /> },
  expired: { color: '#DC2626', background: '#FEE2E2', icon: <X size={14} style={{ color: '#DC2626' }} /> },
  none: { color: '#6B7280', background: '#F3F4F6', icon: <Clock size={14} style={{ color: '#6B7280' }} /> },
};

const formatPhone = (phone) => {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length !== 10) return phone;

  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
}

const isWithin30Days = (createdAt) => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const today = new Date();
  const diffInDays = (today - createdDate) / (1000 * 60 * 60 * 24);
  return diffInDays <= 30;
}

const VehicleList = ({ isActive, query, refreshKey, onListChange, setEditModalOpen, setEditData, setSelectedId, setDeleteVehicleModalOpen, setTenantModalOpen, setTenantDetails }) => {
  const redirect = useHistory();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [sendingId, sendPaymentLink] = useSendPaymentLink(onListChange);
  const canUpdate = isAuthenticated && user["https://propertydek.com/permissions"].indexOf("vehicle:update") > -1;
  const [allData, setAllData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);
  const sentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const loadedKeyRef = useRef(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [sortItem, setSortItem] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [carImage, setCarImage] = useState("");
  const [tenantImage, setTenantImage] = useState("");
  const [openPop, setOpenPop] = useState(false);

  const carPopoverActionRef = useRef(null);
  const tenantPopoverActionRef = useRef(null);

  const handleOpen = () => {
    setOpenPop(true);
  }
  const handleClose = () => {
    setOpenPop(false);
  }

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCarImage("");
  };

  const handlePopoverOpen2 = (event) => {
    setAnchorEl2(event.currentTarget);
  };
  const handlePopoverClose2 = () => {
    setAnchorEl2(null);
    setTenantImage("");
  };

  const open = Boolean(anchorEl);
  const open2 = Boolean(anchorEl2);

  const toggleRow = (rowId) => {
    setExpandedRowId(prev => prev === rowId ? null : rowId);
  };

  // Sorting, search and filters run on the server (v1/vehicles/list); rows arrive in chunks
  const fetchVehicles = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { ...buildFilterParams(query), offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
    if (sortItem !== "") {
      params.sort_by = sortItem;
      params.sort_order = sortOrder;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/vehicles/list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};

      setAllData((prev) => (replace ? newChunk : prev.concat(newChunk)));
      setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
      setHasMore(Boolean(meta?.has_more));

      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Vehicles] Failed to load vehicle list", error);
      toast.error(error?.response?.data?.message || "Unable to load vehicles. Please try again.");

      if (replace) {
        // Allow the next activation of the list view to try again
        loadedKeyRef.current = null;
        setLoadError(true);
        setAllData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, query, sortItem, sortOrder]);

  // Load the first chunk only while the list view is visible, and only if search, filters,
  // sorting or data changed since the last load (switching views back doesn't refetch)
  const requestKey = JSON.stringify([query, sortItem, sortOrder, refreshKey]);
  useEffect(() => {
    if (!isActive || loadedKeyRef.current === requestKey) return;
    loadedKeyRef.current = requestKey;
    fetchVehicles(0, true);
  }, [isActive, requestKey, fetchVehicles]);

  // Infinite scroll: load the next chunk when the sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!isActive || !node) return;

    const observer = new IntersectionObserver((entries) => {
      const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchVehicles(offset, false);
      }
    }, { threshold: 0.1 });

    observer.observe(node);
    return () => observer.disconnect();
  }, [isActive, hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchVehicles]);

  const rowsData = allData.map((item) => {
    const isGuest = Number(item.is_guest) === 1 || item.is_guest === true || item.is_guest === 'true';

    if (isGuest) {
      const fname = item.guest_fname || "";
      const lname = item.guest_lname || "";
      const initials = (fname && lname) ? `${fname[0]}${lname[0]}`.toUpperCase() : "G";
      return {
        id: item.id,
        initials,
        name: `${fname} ${lname}`.trim(),
        phone: formatPhone(item.guest_phone || ""),
        license: item.license,
        make: item.make,
        model: item.model,
        color: item.color,
        property_id: item.property_id,
        is_disabled: item.is_disabled,
        property: item.property_name,
        unit: item.apt_no,
        tenant_image: "",
        tenant_id: item.tenant_id,
        tenant_info_id: item.tenant_info_id,
        apt_id: item.apt_id,
        isNew: isWithin30Days(item.created_at),
        image_path: item.image_path,
        image_thumbail_path: item.image_thumbail_path,
        is_guest: 1,
        // Latest parking pass from the server (status, dates, price)
        pass: guestPassDisplay(item.guest_pass),
        guest_fname: item.guest_fname || "",
        guest_lname: item.guest_lname || "",
        guest_phone: item.guest_phone || "",
        tenant_details: null
      };
    } else {
      return {
        id: item.id,
        initials: item.tenant_initial,
        name: item.tenant_name,
        license: item.license,
        make: item.make,
        model: item.model,
        color: item.color,
        property_id: item.property_id,
        property: item.property_name,
        is_disabled: item.is_disabled,
        unit: item.apt_no,
        tenant_image: item.tenant_image,
        tenant_image2: item.tenant_image2,
        tenant_id: item.tenant_id,
        tenant_info_id: item.tenant_info_id,
        apt_id: item.apt_id,
        isNew: isWithin30Days(item.created_at),
        image_path: item.image_path,
        image_thumbail_path: item.image_thumbail_path,
        is_guest: 0,
        tenant_details: { ...item.tenant_details, tenant_image: item.tenant_image, property: item.property_name, unit: item.apt_no }
      };
    }
  });

  const changeSortOrder = (type) => {
    if (type === sortItem) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortOrder("asc");
    }
    setSortItem(type);
  }

  const goToDetails = (item) => {
    if (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("vehicle:update") > -1) {
      setEditModalOpen(true);
      setEditData(item);
    } else {
      return false;
    }
  }

  const goToTenantetails = (row) => {
    if (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("tenant-contactinfo:read") > -1 && user["https://propertydek.com/permissions"].indexOf("tenant-journey:read") > -1) {
      redirect.push("/people/tenants/details/" + row.tenant_id);
    } else {
      setTenantModalOpen(true);
      setTenantDetails(row?.tenant_details);
    }
  }

  if (contentLoading) {
    return (<div className="Loader TableLoader">
      <TableLoader />
    </div>);
  }

  return (
    <div className={refetching ? `${styles.IncomeListUnderSec} ${styles.Refetching}` : `${styles.IncomeListUnderSec}`}>

      <MobileViewList
        rowsData={rowsData}
        sendingId={sendingId}
        sendPaymentLink={sendPaymentLink}
        setSelectedId={setSelectedId.bind(this)}
        setDeleteVehicleModalOpen={setDeleteVehicleModalOpen.bind(this)}
        setEditData={setEditData.bind(this)}
        setEditModalOpen={setEditModalOpen.bind(this)}
        setTenantModalOpen={setTenantModalOpen.bind(this)}
        setTenantDetails={setTenantDetails.bind(this)}
      />

      <TableContainer className={`${styles.TableDiv} CustomTableDiv`}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={`${styles.TableHead}`} style={{ width: 300 }} onClick={(e) => changeSortOrder("tenant_name")}>
                TENANT / GUEST
                {sortItem === "tenant_name" && sortOrder === "asc" && (<ChevronDown />)}
                {sortItem === "tenant_name" && sortOrder === "desc" && (<ChevronUp />)}
                {sortItem !== "tenant_name" && (<Code className={`${styles.Normal}`} />)}
              </TableCell>
              <TableCell className={`${styles.TableHead}`} style={{ width: 200 }} onClick={(e) => changeSortOrder("license")}>
                LICENSE
                {sortItem === "license" && sortOrder === "asc" && (<ChevronDown />)}
                {sortItem === "license" && sortOrder === "desc" && (<ChevronUp />)}
                {sortItem !== "license" && (<Code className={`${styles.Normal}`} />)}
              </TableCell>
              <TableCell className={`${styles.TableHead}`} style={{ width: 250 }} onClick={(e) => changeSortOrder("make")}>
                VEHICLE
                {sortItem === "make" && sortOrder === "asc" && (<ChevronDown />)}
                {sortItem === "make" && sortOrder === "desc" && <ChevronUp />}
                {sortItem !== "make" && (<Code className={`${styles.Normal}`} />)}
              </TableCell>
              <TableCell className={`${styles.TableHead}`} style={{ width: 250 }} onClick={(e) => changeSortOrder("property_name")}>
                PROPERTY / UNIT
                {sortItem === "property_name" && sortOrder === "asc" && (<ChevronDown />)}
                {sortItem === "property_name" && sortOrder === "desc" && (<ChevronUp />)}
                {sortItem !== "property_name" && (<Code className={`${styles.Normal}`} />)}
              </TableCell>
              <TableCell className={`${styles.TableHead}`} style={{ width: 200 }}>
                PARKING PASS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsData.map((row, index) => {
              const rowClass = index % 2 === 0 ? styles.MainRowOdd : styles.MainRowEven;
              const isExpanded = expandedRowId === row.id;
              const isGuest = row.is_guest === 1;

              // Pass rendering from the server pass (guestPassDisplay)
              const pass = row.pass || guestPassDisplay(null);
              const tone = PASS_TONES[pass.tone] || PASS_TONES.none;
              const passLabel = pass.badge;
              const passSubLabel = pass.subLabel;
              const passColor = tone.color;
              const passBackgroundColor = tone.background;
              const passStatusText = pass.statusText;
              const passStatusIcon = tone.icon;
              const passValidFrom = pass.validFrom;
              const passWasValid = pass.wasValid;
              const passExpires = pass.expires;
              const passRemaining = pass.remaining;
              const alertClass = pass.alertTone === "error" ? styles.AlertBannerError : styles.AlertBannerWarning;
              const alertIcon = pass.alertTone === "error" ? <AlertCircle size={16} /> : <AlertTriangle size={16} />;
              const alertText = pass.alertText;
              const passPrice = pass.price;
              const passName = pass.passName;

              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={rowClass}
                    onClick={(e) => {
                      goToDetails(row);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell className={`${styles.TableBodyText} ${styles.ProfileImg} ${!isGuest ? styles.Hover : ''}`}>
                      {isGuest ? (
                        <div className={styles.TenantInside}>
                          <div className={styles.AvatarCircle} style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                            {row.initials}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <span className={styles.GuestNameText}>{row.name}</span>
                              <span className={styles.GuestBadge}>Guest</span>
                            </div>
                            <span className={styles.GuestPhoneText}>{row.phone}</span>
                          </div>
                        </div>
                      ) : (
                        <Fragment>
                          {row?.tenant_image !== "" ? <img src={row?.tenant_image} alt="" onMouseEnter={(e) => { setTenantImage(row?.tenant_image2); handlePopoverOpen2(e); }} onMouseLeave={handlePopoverClose2} /> : <div className={styles.AvatarCircle} style={{ backgroundColor: '#E0F2FE', color: '#0284C7' }}>
                            {row.initials}
                          </div>}
                          <div style={{ display: 'inline-flex', gap: '4px' }}>
                            <span className={styles.TenantNameText} onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToTenantetails(row); }}>{row.name}</span>
                            {row.isNew && <p className={styles.NewBadgeSmall}>New</p>}
                          </div>
                        </Fragment>
                      )}
                    </TableCell>
                    <TableCell className={styles.TableBodyText}>
                      <div className={`${styles.DisplayFlex} ${styles.LicenseSec}`}>
                        <span>{row.license}</span>
                        {row?.image_thumbail_path !== "" ? <div className={`${styles.CarSec}`} onMouseEnter={(e) => { setCarImage(row?.image_path); handlePopoverOpen(e); }} onMouseLeave={handlePopoverClose}>
                          <img src={row?.image_thumbail_path} alt="" />
                        </div> :
                          <div className={`${styles.NoCarSec}`}>
                            <img src={"/static/img/no-car.png"} alt="" />
                          </div>}
                      </div>
                    </TableCell>
                    <TableCell className={styles.TableBodyText}>
                      <div className={styles.VehicleCell}>
                        <span className={styles.VehicleMakeModel}>{row.make} {row.model}</span>
                        <span className={styles.VehicleColorDot}></span>
                        <span className={styles.VehicleColor}>{row.color}</span>
                      </div>
                    </TableCell>
                    <TableCell className={`${styles.TableBodyText} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + row?.property_id); }}>
                      <div className={styles.PropertyUnitCell}>
                        <span className={styles.PropertyText}>{row.property}</span>
                        <p className={styles.UnitText}>{row.unit}</p>
                      </div>
                    </TableCell>
                    {isGuest ? (
                      <TableCell 
                        className={styles.TableBodyText}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleRow(row.id);
                        }}
                        style={{ cursor: 'pointer', position: 'relative' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: '45px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
                            <span style={{ color: passColor, fontWeight: 500, fontSize: '11px', background: passBackgroundColor, padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap' }}>{passLabel}</span>
                            <span style={{ color: '#9CA3AF', fontSize: '11px', whiteSpace: 'nowrap' }}>{passSubLabel}</span>
                          </div>
                          {isExpanded ? <ChevronUp size={16} style={{ color: '#818CF8', marginLeft: '8px' }} /> : <ChevronDown size={16} style={{ color: '#818CF8', marginLeft: '8px' }} />}
                        </div>
                        {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("vehicle:update") > -1 && (
                          <Dropdown className={`${styles.ActionLeaseBUSec}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                            <Dropdown.Toggle className={`${styles.ActionsLeaseBU} ActionsBU`} id="dropdown-basic">
                              <MoreVertical />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className={`${styles.ActionsBUList} ActionsBUList`}>
                              <Dropdown.Item className={`${styles.EditActionsBUListItem}`} onClick={(e) => { setEditModalOpen(true); setEditData(row); }}>
                                <Edit /> Edit
                              </Dropdown.Item>
                              <Dropdown.Item className={`${styles.EditActionsBUListItem} ${styles.Delete}`} onClick={(e) => { setDeleteVehicleModalOpen(true); setSelectedId(row?.id); }}>
                                <Trash2 /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </TableCell>
                    ) : (
                      <TableCell className={styles.TableBodyText} style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: '45px' }}>
                          {row.is_disabled === 1 ? <span className={styles.DisableBadge}>Disabled</span> : <span className={styles.ActiveBadge}>Active</span>}
                        </div>
                        {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("vehicle:update") > -1 && (
                          <Dropdown className={`${styles.ActionLeaseBUSec}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                            <Dropdown.Toggle className={`${styles.ActionsLeaseBU} ActionsBU`} id="dropdown-basic">
                              <MoreVertical />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className={`${styles.ActionsBUList} ActionsBUList`}>
                              <Dropdown.Item className={`${styles.EditActionsBUListItem}`} onClick={(e) => { setEditModalOpen(true); setEditData(row); }}>
                                <Edit /> Edit
                              </Dropdown.Item>
                              <Dropdown.Item className={`${styles.EditActionsBUListItem} ${styles.Delete}`} onClick={(e) => { setDeleteVehicleModalOpen(true); setSelectedId(row?.id); }}>
                                <Trash2 /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  {isExpanded && isGuest && (
                    <TableRow className={styles.ExpandedRow}>
                      <TableCell colSpan={5} className={styles.ExpandedCell} style={{ padding: '16px 24px' }}>
                        <div className={styles.PassDetailsContainer}>
                          <div className={styles.PassDetailsHeaderRow}>
                            <div className={styles.PassDetailsCol}>
                              <span className={styles.PassDetailLabel}>PASS TYPE</span>
                              <span className={styles.PassDetailValue}>{passName}{passPrice !== null ? ` · $${passPrice.toFixed(2)}` : ""}</span>
                            </div>
                            <div className={styles.PassDetailsCol}>
                              <span className={styles.PassDetailLabel}>STATUS</span>
                              <span className={styles.PassDetailValue} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: passColor }}>
                                {passStatusIcon}
                                {passStatusText}
                              </span>
                            </div>
                            {passValidFrom && (
                              <div className={styles.PassDetailsCol}>
                                <span className={styles.PassDetailLabel}>VALID FROM</span>
                                <span className={styles.PassDetailValue}>{passValidFrom}</span>
                              </div>
                            )}
                            {passWasValid && (
                              <div className={styles.PassDetailsCol}>
                                <span className={styles.PassDetailLabel}>WAS VALID</span>
                                <span className={styles.PassDetailValue}>{passWasValid}</span>
                              </div>
                            )}
                            {passExpires && (
                              <div className={styles.PassDetailsCol}>
                                <span className={styles.PassDetailLabel}>EXPIRES</span>
                                <span className={styles.PassDetailValue}>{passExpires}</span>
                              </div>
                            )}
                            {passRemaining && (
                              <div className={styles.PassDetailsCol}>
                                <span className={styles.PassDetailLabel}>REMAINING</span>
                                <span className={styles.PassDetailValue}>{passRemaining}</span>
                              </div>
                            )}
                            <div className={styles.PassActionButtons}>
                              {pass.canSend && canUpdate && (<button className={styles.SendRenewalButton} disabled={sendingId === row.id} onClick={(e) => { e.preventDefault(); e.stopPropagation(); sendPaymentLink(row.id); }}>
                                <Send size={14} />
                                {sendingId === row.id ? "Sending..." : pass.sendLabel}
                              </button>)}
                              <button className={styles.RevokeButton} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedId(row.id); setDeleteVehicleModalOpen(true); }}>
                                {pass.revokeLabel}
                              </button>
                            </div>
                          </div>
                          {alertText && (<div className={alertClass}>
                            {alertIcon}
                            <span>{alertText}</span>
                          </div>)}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sentinel: entering the viewport loads the next chunk (table on desktop, cards on mobile) */}
      <div ref={sentinelRef} className={`${styles.ScrollSentinel}`} />

      {loadingMore && (<div className={`${styles.ScrollStatus}`}>
        <p>Loading more vehicles...</p>
      </div>)}

      {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
        <p>Couldn't load more vehicles.</p>
        <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchVehicles(offset, false)}>Retry</button>
      </div>)}

      {loadError && (<div className={`${styles.NoDataMain}`}>
        <p className={`${styles.NoDataText}`}>Something went wrong!</p>
        <p className={`${styles.NoDataTextSub}`}>we couldn't load the vehicles.</p>
        <button type="button" className={`${styles.RetryBu}`} onClick={() => { loadedKeyRef.current = requestKey; fetchVehicles(0, true); }}>Retry</button>
      </div>)}

      {!loadError && rowsData.length === 0 && (<div className={`${styles.NoDataMain}`}>
        <div className={`${styles.NoDataIMG}`}>
          <img src="/static/img/no-car.png" alt="No Data" />
        </div>
        <p className={`${styles.NoDataText}`}>Unfortunately!</p>
        <p className={`${styles.NoDataTextSub}`}>
          we couldn't find any vehicle at this time.
        </p>
      </div>)}

      <Popover
        id="mouse-over-popover"
        sx={{ pointerEvents: "none" }}
        open={open}
        anchorEl={anchorEl}
        action={carPopoverActionRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div className={`${styles.PopupImgSec}`}>
          <img
            src={carImage}
            alt=""
            onLoad={() => carPopoverActionRef.current?.updatePosition()}
          />
        </div>
      </Popover>

      <Popover
        id="mouse-over-popover2"
        sx={{ pointerEvents: "none" }}
        open={open2}
        anchorEl={anchorEl2}
        action={tenantPopoverActionRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handlePopoverClose2}
        disableRestoreFocus
      >
        <div className={`${styles.PopupImgSec}`}>
          <img
            src={tenantImage}
            alt=""
            style={{ maxWidth: '400px', maxHeight: '600px' }}
            onLoad={() => tenantPopoverActionRef.current?.updatePosition()}
          />
        </div>
      </Popover>

      <Dialog
        open={openPop}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClose()
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className={`${styles.PopupImgSec}`}>
          <img src={carImage} alt="" />
          <button onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose()
          }}><X /></button>
        </div>
      </Dialog>

    </div>
  );
}

export default VehicleList;